import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuración por defecto - Electric Obsidian Theme
const defaultConfig = {
  // Colores principales
  theme_color: 'var(--brand-primary)',
  secondary_color: '#00F0FF',
  accent_color: 'var(--brand-primary-strong)',
  bg_color: '#0a0a0a',
  card_bg_color: 'rgba(26, 26, 26, 0.8)',
  text_color: '#ffffff',
  text_muted_color: 'rgba(255, 255, 255, 0.6)',
  // Colores de texto según fondo
  text_on_dark: '#FFFFFF',
  text_on_light: '#1a1a1a',
  
  // Gradientes
  gradient_start: 'var(--brand-primary)',
  gradient_end: 'var(--brand-primary-strong)',
  
  // Fuentes
  heading_font: 'Poppins',
  body_font: 'Poppins',
  primary_font: 'Poppins',
  
  // Estilos
  button_style: 'rounded-full',
  card_style: 'rounded-3xl',
  border_radius: '24px',
  
  // Imágenes
  hero_image: '',
  hero_image_female: '',  // Foto de bienvenida específica para mujeres
  hero_image_male: '',    // Foto de bienvenida específica para hombres
  logo_url: '',
  about_text: '',
  
  // Imágenes de programas
  program_diet_image: '',
  program_workout_image: '',
  program_progress_image: '',
  program_menu_image: '',
  program_recipes_image: '',
  program_data_image: '',
  program_videos_image: '',
  program_ebooks_image: ''
};

const SiteConfigContext = createContext(null);

export const useSiteConfig = () => {
  const context = useContext(SiteConfigContext);
  if (!context) {
    return { config: defaultConfig, loading: false, refreshConfig: () => {} };
  }
  return context;
};

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/site-config`);
      const data = response.data;
      const mergedConfig = {
        ...defaultConfig,
        ...data
      };
      setConfig(mergedConfig);
      
      // Aplicar estilos CSS globalmente
      applyGlobalStyles(mergedConfig);
    } catch (error) {
      console.error('Error fetching site config:', error);
      applyGlobalStyles(defaultConfig);
    } finally {
      setLoading(false);
    }
  }, []);

  const applyGlobalStyles = (cfg) => {
    // Crear o actualizar el elemento de estilo global
    let styleEl = document.getElementById('site-config-styles');
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = 'site-config-styles';
      document.head.appendChild(styleEl);
    }

    const themeColor = cfg.theme_color || 'var(--brand-primary)';
    const secondaryColor = cfg.secondary_color || '#00F0FF';
    const accentColor = cfg.accent_color || 'var(--brand-primary-strong)';
    const bgColor = cfg.bg_color || '#0a0a0a';
    const textColor = cfg.text_color || '#ffffff';
    const textOnDark = cfg.text_on_dark || '#FFFFFF';
    const textOnLight = cfg.text_on_light || '#1a1a1a';
    const gradientStart = cfg.gradient_start || themeColor;
    const gradientEnd = cfg.gradient_end || accentColor;
    const headingFont = cfg.heading_font || 'Poppins';
    const bodyFont = cfg.body_font || 'Poppins';

    // Aplicar variables CSS
    const styles = `
      :root {
        --theme-color: ${themeColor};
        --secondary-color: ${secondaryColor};
        --accent-color: ${accentColor};
        --bg-color: ${bgColor};
        --text-color: ${textColor};
        --text-on-dark: ${textOnDark};
        --text-on-light: ${textOnLight};
        --gradient-start: ${gradientStart};
        --gradient-end: ${gradientEnd};
        --heading-font: '${headingFont}', sans-serif;
        --body-font: '${bodyFont}', sans-serif;
        --primary-font: '${headingFont}', sans-serif;
        --card-bg: rgba(26, 26, 26, 0.8);
        --border-color: rgba(255, 255, 255, 0.1);
      }

      /* ========================================
         FUENTES GLOBALES
         ======================================== */
      body {
        font-family: var(--body-font);
        background-color: var(--bg-color);
        color: var(--text-color);
      }

      h1, h2, h3, h4, h5, h6, .heading, .title-heading, [class*="font-heading"] {
        font-family: var(--heading-font) !important;
        font-weight: 700;
      }

      h1 {
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }

      button, .btn, [class*="Button"] {
        font-family: var(--primary-font);
      }

      /* ========================================
         CLASES DE UTILIDAD PARA COLORES
         ======================================== */
      
      /* Fondos */
      .theme-bg {
        background-color: var(--theme-color) !important;
      }

      .theme-bg-gradient {
        background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end)) !important;
      }

      .theme-bg-secondary {
        background-color: var(--secondary-color) !important;
      }

      /* Textos */
      .theme-text {
        color: var(--theme-color) !important;
      }

      .theme-text-secondary {
        color: var(--secondary-color) !important;
      }

      /* Textos según fondo */
      .text-on-dark {
        color: var(--text-on-dark) !important;
      }

      .text-on-light {
        color: var(--text-on-light) !important;
      }

      /* Bordes */
      .theme-border {
        border-color: var(--theme-color) !important;
      }

      .theme-border-secondary {
        border-color: var(--secondary-color) !important;
      }

      /* Gradientes de texto */
      .theme-text-gradient {
        background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      /* ========================================
         BOTONES METALIZADOS CON COLORES DEL TEMA
         ======================================== */
      .btn-metallic {
        background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end)) !important;
        color: white !important;
        border-radius: 9999px !important;
        font-weight: 600 !important;
        transition: all 0.2s ease !important;
      }

      .btn-metallic:hover {
        opacity: 0.9 !important;
        transform: scale(1.02) !important;
      }

      .btn-outline-theme {
        background: transparent !important;
        border: 2px solid var(--theme-color) !important;
        color: var(--theme-color) !important;
        border-radius: 9999px !important;
        font-weight: 600 !important;
        transition: all 0.2s ease !important;
      }

      .btn-outline-theme:hover {
        background: var(--theme-color) !important;
        color: white !important;
      }

      /* ========================================
         TARJETAS CON TEMA
         ======================================== */
      .card-theme {
        background: var(--card-bg) !important;
        border: 1px solid var(--border-color) !important;
        border-radius: 24px !important;
        backdrop-filter: blur(10px) !important;
      }

      .card-theme-popular {
        border: 2px solid var(--theme-color) !important;
      }

      /* ========================================
         OVERRIDE COLORES HARDCODEADOS
         ======================================== */
      
      /* Reemplazar var(--brand-primary) con variable del tema */
      [style*="var(--brand-primary)"], [style*="#ff2e93"] {
        --override-pink: var(--theme-color);
      }

      /* Badges y elementos pequeños */
      .badge-theme {
        background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end)) !important;
        color: white !important;
      }

      /* Checks y iconos */
      .icon-theme {
        color: var(--theme-color) !important;
      }

      /* ========================================
         INPUTS CON TEMA
         ======================================== */
      input:focus, textarea:focus, select:focus {
        border-color: var(--theme-color) !important;
        box-shadow: 0 0 20px rgba(var(--theme-color), 0.3) !important;
      }

      /* ========================================
         SCROLLBAR PERSONALIZADO
         ======================================== */
      ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      ::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.05);
      }

      ::-webkit-scrollbar-thumb {
        background: var(--theme-color);
        border-radius: 4px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: var(--accent-color);
      }

      /* ========================================
         SELECCIÓN DE TEXTO
         ======================================== */
      ::selection {
        background: var(--theme-color);
        color: white;
      }
    `;

    styleEl.textContent = styles;

    // También actualizar las variables CSS en el :root directamente
    document.documentElement.style.setProperty('--theme-color', themeColor);
    document.documentElement.style.setProperty('--secondary-color', secondaryColor);
    document.documentElement.style.setProperty('--accent-color', accentColor);
    document.documentElement.style.setProperty('--gradient-start', gradientStart);
    document.documentElement.style.setProperty('--gradient-end', gradientEnd);
    document.documentElement.style.setProperty('--bg-color', bgColor);
    document.documentElement.style.setProperty('--text-color', textColor);
    document.documentElement.style.setProperty('--text-on-dark', textOnDark);
    document.documentElement.style.setProperty('--text-on-light', textOnLight);
  };

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const refreshConfig = () => {
    fetchConfig();
  };

  return (
    <SiteConfigContext.Provider value={{ config, loading, refreshConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export default SiteConfigContext;
